import { Controller } from "@hotwired/stimulus"

export interface ComboboxOption {
  value: string;
  label: string;
  detail?: string | null;
}

const SEARCH_DELAY_MS = 200
const BLUR_CLOSE_DELAY_MS = 150

export default class extends Controller<HTMLElement> {
  static targets = ["input", "listbox", "selections", "inputs", "optionTemplate", "chipTemplate", "emptyTemplate"]
  static values = {
    url: String,
    options: { type: Array, default: [] as ComboboxOption[] },
    selected: { type: Array, default: [] as ComboboxOption[] },
    multiple: { type: Boolean, default: false },
    name: String
  }

  declare readonly inputTarget: HTMLInputElement
  declare readonly listboxTarget: HTMLUListElement
  declare readonly selectionsTarget: HTMLUListElement
  declare readonly inputsTarget: HTMLElement
  declare readonly optionTemplateTarget: HTMLTemplateElement
  declare readonly chipTemplateTarget: HTMLTemplateElement
  declare readonly emptyTemplateTarget: HTMLTemplateElement
  declare readonly urlValue: string
  declare readonly optionsValue: ComboboxOption[]
  declare readonly selectedValue: ComboboxOption[]
  declare readonly multipleValue: boolean
  declare readonly nameValue: string

  private readonly chosen = new Map<string, ComboboxOption>()
  private results: ComboboxOption[] = []
  private activeIndex = -1
  private searchTimer?: number
  private closeTimer?: number
  private request?: AbortController

  connect(): void {
    this.selectedValue.forEach((option) => { this.choose(option) })
  }

  disconnect(): void {
    window.clearTimeout(this.searchTimer)
    window.clearTimeout(this.closeTimer)
    this.request?.abort()
  }

  get selectedOptions(): ComboboxOption[] {
    return [...this.chosen.values()]
  }

  focusInput(): void {
    this.inputTarget.focus()
  }

  search(): void {
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => { void this.loadResults() }, this.urlValue === "" ? 0 : SEARCH_DELAY_MS)
  }

  navigate(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.isOpen() ? this.activate(this.activeIndex + 1) : this.search()
        break
      case "ArrowUp":
        event.preventDefault()
        this.activate(this.activeIndex - 1)
        break
      case "Enter":
        if (!this.isOpen()) return
        event.preventDefault()
        this.chooseActive()
        break
      case "Escape":
        if (!this.isOpen()) return
        event.preventDefault()
        this.close()
        break
      case "Backspace":
        if (this.inputTarget.value === "" && this.multipleValue) this.removeLast()
        break
      default:
        break
    }
  }

  closeSoon(): void {
    this.closeTimer = window.setTimeout(() => { this.close() }, BLUR_CLOSE_DELAY_MS)
  }

  remove(value: string): void {
    if (!this.chosen.delete(value)) return

    const chips = [...this.selectionsTarget.children] as HTMLElement[]
    const inputs = [...this.inputsTarget.querySelectorAll("input")]
    chips.find((chip) => chip.dataset.value === value)?.remove()
    inputs.find((input) => input.value === value)?.remove()
    this.announceChange()
  }

  private async loadResults(): Promise<void> {
    const query = this.inputTarget.value.trim()
    const found = this.urlValue === "" ? this.filterOptions(query) : await this.fetchOptions(query)
    if (found === null) return

    this.results = found.filter((option) => !this.chosen.has(String(option.value)))
    this.renderResults()
  }

  private filterOptions(query: string): ComboboxOption[] {
    const needle = query.toLowerCase()
    return this.optionsValue.filter((option) => `${option.label} ${option.detail ?? ""}`.toLowerCase().includes(needle))
  }

  private async fetchOptions(query: string): Promise<ComboboxOption[] | null> {
    this.request?.abort()
    this.request = new AbortController()
    const url = new URL(this.urlValue, window.location.href)
    url.searchParams.set("q", query)
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: this.request.signal })
      return response.ok ? await response.json() as ComboboxOption[] : []
    } catch {
      return null
    }
  }

  private renderResults(): void {
    this.activeIndex = -1
    this.inputTarget.removeAttribute("aria-activedescendant")
    const items = this.results.length === 0 ? [this.emptyItem()] : this.results.map((option, index) => this.optionItem(option, index))
    this.listboxTarget.replaceChildren(...items)
    this.open()
  }

  private optionItem(option: ComboboxOption, index: number): HTMLElement {
    const item = this.cloneTemplate(this.optionTemplateTarget)
    item.id = `${this.listboxTarget.id}-${index}`
    this.fill(item, "label", option.label)
    this.fill(item, "detail", option.detail ?? "")
    item.addEventListener("mousedown", (event) => { event.preventDefault() })
    item.addEventListener("click", () => { this.chooseAt(index) })
    return item
  }

  private emptyItem(): HTMLElement {
    return this.cloneTemplate(this.emptyTemplateTarget)
  }

  private activate(index: number): void {
    const items = this.optionItems()
    if (items.length === 0) return

    this.activeIndex = (index + items.length) % items.length
    items.forEach((item, position) => { item.dataset.active = String(position === this.activeIndex) })
    const active = items[this.activeIndex]
    this.inputTarget.setAttribute("aria-activedescendant", active.id)
    active.scrollIntoView({ block: "nearest" })
  }

  private chooseActive(): void {
    if (this.activeIndex >= 0) this.chooseAt(this.activeIndex)
  }

  private chooseAt(index: number): void {
    const option = this.results[index]
    if (option === undefined) return

    if (!this.multipleValue) this.selectedOptions.forEach((selected) => { this.remove(selected.value) })
    this.choose(option)
    this.inputTarget.value = ""
    this.announceChange()
    this.multipleValue ? this.search() : this.close()
    this.inputTarget.focus()
  }

  private choose(option: ComboboxOption): void {
    const value = String(option.value)
    if (this.chosen.has(value)) return

    this.chosen.set(value, { ...option, value })
    this.selectionsTarget.append(this.chip(value, option.label))
    this.inputsTarget.append(this.hiddenInput(value))
  }

  private chip(value: string, label: string): HTMLElement {
    const chip = this.cloneTemplate(this.chipTemplateTarget)
    chip.dataset.value = value
    this.fill(chip, "label", label)
    const button = chip.querySelector<HTMLButtonElement>("[data-field='remove']")
    button?.setAttribute("aria-label", `Remove ${label}`)
    button?.addEventListener("click", (event) => {
      event.stopPropagation()
      this.remove(value)
      this.inputTarget.focus()
    })
    return chip
  }

  private hiddenInput(value: string): HTMLInputElement {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.nameValue
    input.value = value
    return input
  }

  private removeLast(): void {
    const last = this.selectedOptions.at(-1)
    if (last !== undefined) this.remove(last.value)
  }

  private announceChange(): void {
    this.dispatch("change", { detail: { selected: this.selectedOptions } })
  }

  private open(): void {
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  private close(): void {
    this.listboxTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  private isOpen(): boolean {
    return !this.listboxTarget.hidden
  }

  private optionItems(): HTMLElement[] {
    return [...this.listboxTarget.querySelectorAll<HTMLElement>("[role='option']")]
  }

  private cloneTemplate(template: HTMLTemplateElement): HTMLElement {
    return (template.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement
  }

  private fill(element: HTMLElement, field: string, text: string): void {
    const target = element.querySelector<HTMLElement>(`[data-field='${field}']`)
    if (target !== null) target.textContent = text
  }
}
